import L from "leaflet";
import type { MapObject } from "@fortex/api";
import type { MapCoreEvents, SelectionApi } from "../core/api";
import type { Emitter } from "../core/events";
import type { ResolvedOptions } from "../core/options";
import type { MapStore } from "../core/store";
import { mapObjectFromFeature } from "../features/adapters";
import { featureObjectId } from "../features/FeatureIndex";
import type { FeatureIndex } from "../features/FeatureIndex";
import type { FeatureRenderer } from "../features/FeatureRenderer";
import type { Feature } from "../features/types";

export const boxThresholdPixels = 4;

export type SelectionControllerOptions = {
  map: L.Map;
  container: HTMLElement;
  store: MapStore;
  options: ResolvedOptions["selection"];
  index: FeatureIndex;
  renderer: FeatureRenderer;
  events: Emitter<MapCoreEvents>;
};

export type SelectionController = SelectionApi & {
  onFeatureClick(feature: Feature, originalEvent: MouseEvent | null): void;
  onMapClick(): void;
  reapply(): void;
  handleEscape(): boolean;
  boxActive(): boolean;
  dispose(): void;
};

function unique(ids: Iterable<number>): number[] {
  return [...new Set(ids)];
}

function sameIds(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export function isAdditive(event: Event | null): boolean {
  if (event === null || !("ctrlKey" in event)) return false;

  const keyed = event as MouseEvent;

  return keyed.ctrlKey || keyed.metaKey;
}

export function createSelection(settings: SelectionControllerOptions): SelectionController {
  const { map, container, store, options, index, renderer, events } = settings;
  const known = new Map<number, MapObject>();
  let ids: number[] = [];
  let box: { start: L.Point; element: HTMLElement } | null = null;
  let suppressMapClick = false;

  const objectsOf = (): MapObject[] => {
    const result: MapObject[] = [];

    for (const id of ids) {
      const feature = index.byObjectId(id);
      const object = feature === null ? known.get(id) ?? null : mapObjectFromFeature(feature);

      if (object !== null) {
        known.set(id, object);
        result.push(object);
      }
    }

    return result;
  };

  const keysOf = (): string[] => ids.map(id => index.byObjectId(id)?.key).filter((a): a is string => a !== undefined);

  const apply = (emit: boolean): void => {
    renderer.setSelected(keysOf());

    const payload = { ids: [...ids], objects: objectsOf() };

    store.setState({ selection: payload });

    if (emit) events.emit("selection:change", payload);
  };

  const set = (next: number[]): void => {
    if (!options.enabled) return;

    const cleaned = unique(next);

    if (sameIds(cleaned, ids)) return;

    ids = options.multi ? cleaned : cleaned.slice(0, 1);
    apply(true);
  };

  const clear = (): void => set([]);

  const toggle = (id: number): void => set(ids.includes(id) ? ids.filter(a => a !== id) : [...ids, id]);

  const removeBox = (): void => {
    if (box === null) return;

    box.element.remove();
    box = null;
    document.removeEventListener("mousemove", onBoxMove);
    document.removeEventListener("mouseup", onBoxUp);
  };

  const boxRectangle = (current: L.Point): { min: L.Point; max: L.Point } | null => {
    if (box === null) return null;

    return {
      min: L.point(Math.min(box.start.x, current.x), Math.min(box.start.y, current.y)),
      max: L.point(Math.max(box.start.x, current.x), Math.max(box.start.y, current.y))
    };
  };

  const onBoxMove = (event: MouseEvent): void => {
    if (box === null) return;

    const rectangle = boxRectangle(map.mouseEventToContainerPoint(event));

    if (rectangle === null) return;

    box.element.style.left = `${rectangle.min.x}px`;
    box.element.style.top = `${rectangle.min.y}px`;
    box.element.style.width = `${rectangle.max.x - rectangle.min.x}px`;
    box.element.style.height = `${rectangle.max.y - rectangle.min.y}px`;
  };

  const onBoxUp = (event: MouseEvent): void => {
    if (box === null) return;

    const current = map.mouseEventToContainerPoint(event);
    const rectangle = boxRectangle(current);
    const start = box.start;

    removeBox();

    if (rectangle === null || (Math.abs(current.x - start.x) < boxThresholdPixels && Math.abs(current.y - start.y) < boxThresholdPixels)) return;

    suppressMapClick = true;
    setTimeout(() => {
      suppressMapClick = false;
    }, 0);

    const bounds = L.latLngBounds(map.containerPointToLatLng(rectangle.min), map.containerPointToLatLng(rectangle.max));
    const found = index
      .intersecting(bounds)
      .map(featureObjectId)
      .filter((a): a is number => a !== null);

    set(isAdditive(event) && options.multi ? [...ids, ...found] : found);
  };

  const onBoxDown = (event: MouseEvent): void => {
    if (!event.shiftKey || event.button !== 0 || box !== null) return;

    event.preventDefault();

    const element = document.createElement("div");

    element.className = "fxmap-selection-box";
    element.setAttribute("data-fxmap", "selection-box");
    container.appendChild(element);
    box = { start: map.mouseEventToContainerPoint(event), element };
    onBoxMove(event);
    document.addEventListener("mousemove", onBoxMove);
    document.addEventListener("mouseup", onBoxUp);
  };

  if (options.enabled && options.box) {
    map.boxZoom?.disable();
    container.addEventListener("mousedown", onBoxDown);
  }

  return {
    get: () => [...ids],
    getObjects: objectsOf,
    set,
    add: next => set([...ids, ...next]),
    toggle,
    clear,
    onFeatureClick: (feature, originalEvent) => {
      if (!options.enabled || feature.cluster) return;

      const id = featureObjectId(feature);

      if (id === null) return;

      const object = mapObjectFromFeature(feature);

      if (object !== null) known.set(id, object);

      if (options.multi && isAdditive(originalEvent)) toggle(id);
      else set([id]);
    },
    onMapClick: () => {
      if (suppressMapClick) return;

      clear();
    },
    reapply: () => {
      if (ids.length > 0) apply(false);
    },
    handleEscape: () => {
      if (box !== null) {
        removeBox();
        return true;
      }

      if (ids.length === 0) return false;

      clear();

      return true;
    },
    boxActive: () => box !== null,
    dispose: () => {
      removeBox();
      container.removeEventListener("mousedown", onBoxDown);
      known.clear();
    }
  };
}
